
import { useState, useEffect, useRef } from 'react';

export default function ExperienceSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const experiences = [
    {
      company: '비즈톡',
      position: '서버 개발자',
      period: '2024.04 ~ 현재',
      type: 'current',
      achievements: [
        '레거시 API 서버 구조 개선 및 신규 개발',
        '통계 서비스 성능 개선 (8000ms → 1000ms, 87.5% 감소)',
        'OAuth 로그인(카카오·네이버·카페24) 게이트웨이 구축 및 B2B 서비스화',
        '결제 시스템(PG 연동 · 포인트 충전/차감/환불/복구) 개발',
        '챗봇 관리 웹서비스 백엔드/프론트엔드 개발',
        'UI 개선 및 메인 페이지 애니메이션 구현'
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-[#0d1117]">
      <div className="max-w-6xl mx-auto">
        <div className={`transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] bg-clip-text text-transparent">
              Experience
            </span>
          </h2>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#3ECF8E] to-[#2dd4bf]" />

            {experiences.map((exp, index) => (
              <div
                key={index}
                className={`relative pl-20 pb-16 transition-all duration-700 ${
                  isVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 300}ms` }}
              >
                {/* Timeline Dot */}
                <div className="absolute left-6 top-2 w-4 h-4 bg-[#3ECF8E] rounded-full border-4 border-[#0d1117] shadow-lg shadow-[#3ECF8E]/50" />

                {/* Experience Card */}
                <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10">
                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{exp.company}</h3>
                      <p className="text-xl text-[#3ECF8E] font-medium">{exp.position}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-sm text-gray-300 font-mono">
                        {exp.period}
                      </span>
                      {exp.type === 'current' && (
                        <span className="px-3 py-1 bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] text-black text-sm font-medium rounded-full">
                          현재 재직
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Achievements */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-white mb-4">주요 성과</h4>
                    <div className="grid gap-3">
                      {exp.achievements.map((achievement, achIndex) => (
                        <div
                          key={achIndex}
                          className={`flex items-start gap-3 p-4 bg-[#0d1117] border border-[#30363d] rounded-lg hover:border-[#3ECF8E]/30 transition-all duration-300 ${
                            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                          }`}
                          style={{ transitionDelay: `${(index * 300) + (achIndex * 100)}ms` }}
                        >
                          <i className="ri-arrow-right-s-line text-[#3ECF8E] mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 leading-relaxed">
                            {achievement.includes('87.5% 감소') ? (
                              <>
                                통계 서비스 성능 개선 (8000ms → 1000ms,{' '}
                                <span className="px-2 py-1 bg-[#3ECF8E]/20 text-[#3ECF8E] rounded font-semibold">
                                  87.5% 감소
                                </span>
                                )
                              </>
                            ) : (
                              achievement
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tech Stack Used */}
                  <div className="mt-6 pt-6 border-t border-[#30363d]">
                    <h4 className="text-sm font-semibold text-gray-400 mb-3">주요 기술 스택</h4>
                    <div className="flex flex-wrap gap-2">
                      {['Spring Boot', 'Java', 'MySQL', 'Redis', 'AWS', 'Docker', 'Jenkins', 'React', 'Angular'].map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 bg-[#0d1117] border border-[#30363d] rounded-md text-xs text-gray-400 hover:border-[#3ECF8E]/50 hover:text-[#3ECF8E] transition-all duration-300"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
